import {computed, ref} from 'vue';
import {useRoute} from '@typed-router';
import {defineStore} from 'pinia';
import {useFormat, useLocale, useLogger, useTranslation} from '#imports';
import {pushVJoySnackbar} from '@maltjoy/core-vue';
import type {CreateExpenseRequest, Currency, ExpenseListResource, ExpenseProjectInfoResource, ExpenseResource, Tax} from '@api';
import {FetchError, TaxType} from '@api';
import {matchEnum, isNumberValid} from '~/common/validators';
import type {CreateExpenseRequestFormState, FormattedExpenseTableItem} from '../types/expense.types';
import {ProjectCurrencyTypeMapper, UpdateOrCreateExpenseErrors} from '../types/expense.types';
import {uploadFiles} from '@/common/validators/utils/upload.utils';
import {expensesTrackerEndpoints} from '@/registry';

export const useExpensesStore = defineStore('expenses-tracker', () => {
    const route = useRoute();
    const projectId = computed(() => (route.params as {projectId?: string}).projectId as string);

    const {t} = useTranslation();

    const {locale} = useLocale();
    const {formatAmount, formatDate, formatDateToShort} = useFormat();

    const logger = useLogger();
    const expenseList = ref<ExpenseListResource>(null as unknown as ExpenseListResource);
    const loading = ref(false);
    const projectSettings = ref<ExpenseProjectInfoResource>();

    const projectSummary = computed(() => expenseList.value?.summary);

    const vatTaxApplicableConfig = computed(() => projectSettings.value?.applicableTaxes.find((tax) => tax.type === TaxType.Vat) as Tax);

    const vatChoiceInSettings = computed(() => vatTaxApplicableConfig.value?.defaultPercent);
    const ruleForVatEditing = computed(() => vatTaxApplicableConfig.value?.editableRule);
    const eligibleVatOptions = computed(() => vatTaxApplicableConfig.value.eligiblePercents);

    async function fetchProjectSettings() {
        try {
            projectSettings.value = await expensesTrackerEndpoints.getProjectExpenseInfo({projectId: projectId.value});
        } catch (e) {
            return Promise.reject(e);
        }
    }

    function handleUpdateOrCreateError(e: unknown) {
        if (e instanceof FetchError) {
            if ('data' in e.cause && Array.isArray(e.cause.data)) {
                e.cause.data.forEach((item) => {
                    let message;
                    const errorCode = item?.errorCode;

                    if (matchEnum(UpdateOrCreateExpenseErrors, errorCode)) {
                        message = t(`projectfreelancer.projectview.expenses.create.submit.error.${errorCode}`);
                    } else {
                        message = t('projectfreelancer.projectview.expenses.create.submit.error');
                    }
                    pushVJoySnackbar({props: {message, level: 'error', duration: 'forever'}});
                });
            } else {
                pushVJoySnackbar({
                    props: {message: t('projectfreelancer.projectview.expenses.create.submit.error'), level: 'error', duration: 'forever'},
                });
            }
        } else {
            pushVJoySnackbar({
                props: {message: t('projectfreelancer.projectview.expenses.create.submit.error'), level: 'error', duration: 'forever'},
            });
        }
    }

    async function fetchExpensesForProject(projectId: string) {
        try {
            loading.value = true;
            expenseList.value = await expensesTrackerEndpoints.listProjectExpenses({projectId});
        } catch (e) {
            return Promise.reject(e);
        } finally {
            loading.value = false;
        }
    }

    function formatAmountWithDecimals(amount: number | undefined, currency: Currency, decimals = 2) {
        return amount && !isNaN(amount) ? formatAmount(currency, amount, {decimals}) : '-';
    }

    const formattedExpenses = computed<FormattedExpenseTableItem[]>(() => {
        return (
            expenseList.value?.expenses.map((expense: ExpenseResource) => {
                return {
                    id: expense.expenseId,
                    status: expense.status,
                    total: formatAmountWithDecimals(expense.amountTotal, expense.currency),
                    rawTotal: expense.amountTotal,
                    supplier: expense.supplier,
                    date: expense.submissionDate ? formatDateToShort(expense.submissionDate) : '-',
                    rawDate: expense.submissionDate,
                    purchaseDate: expense.purchaseDate ? formatDate(expense.purchaseDate) : '-',
                    category: expense.category,
                    action:
                        expense.status === 'SUBMITTED' || expense.status === 'BATCH_CREATED' || expense.status === 'APPROVAL_BLOCKED'
                            ? 'EDIT'
                            : 'DOWNLOAD_INVOICE',
                    description: expense.description ?? '-',
                    amountWithoutTaxes: isNumberValid(expense.amountWithoutTaxes)
                        ? formatAmountWithDecimals(expense.amountWithoutTaxes, expense.currency)
                        : '-',
                    amountWithTaxes: isNumberValid(expense.amountWithTaxes)
                        ? formatAmountWithDecimals(expense.amountWithTaxes, expense.currency)
                        : '-',
                    taxOnExpense:
                        isNumberValid(expense.amountWithTaxes) &&
                        isNumberValid(expense.amountWithoutTaxes) &&
                        expense.amountWithTaxes > expense.amountWithoutTaxes
                            ? formatAmountWithDecimals(expense.amountWithTaxes - expense.amountWithoutTaxes, expense.currency)
                            : '-',
                    vat: formatAmountWithDecimals(expense.amountTotal - expense.amountWithTaxesMinusVatDeductible, expense.currency),
                    vatDeductible: expense.vatDeductible,
                    vatDeductibleRate: expense.vatDeductibleRate,
                    totalAmountInvoicedExclTax: expense.amountWithTaxesMinusVatDeductible
                        ? formatAmountWithDecimals(expense.amountWithTaxesMinusVatDeductible, expense.currency)
                        : '-',
                    receiptName: expense.receiptName,
                    receiptId: expense.receiptId,
                    receiptDownloadUrl: expense.receiptDownloadUrl,
                    receiptUploadDate: expense.receiptUploadDate ? formatDate(expense.receiptUploadDate) : '-',
                    approvalBlockedComment: expense.approvalBlockedComment ?? '',
                    distance: expense.distance && expense.distance > 0 ? `${expense.distance} km` : '-',
                    distanceRate:
                        expense.distanceRate && !isNaN(expense.distanceRate)
                            ? formatAmount(expense.currency, expense.distanceRate, {decimals: 4})
                            : '-',
                } satisfies FormattedExpenseTableItem;
            }) ?? []
        );
    });

    const blockedApprovalNotificationCount = computed(
        () => expenseList.value?.expenses?.filter((expense) => expense.status === 'APPROVAL_BLOCKED').length ?? 0,
    );

    async function createExpense(form: CreateExpenseRequestFormState): Promise<ExpenseResource> {
        try {
            const {receiptFile, freelancerTaxes, ...restForm} = form;
            const createExpenseRequestPayload = restForm as CreateExpenseRequest;
            if (receiptFile.length && receiptFile[0] instanceof File) {
                const [fileId] = await uploadFiles(receiptFile);
                createExpenseRequestPayload.receiptId = fileId;
                createExpenseRequestPayload.receiptName = receiptFile[0].name;
                createExpenseRequestPayload.receiptUploadDate = new Date();
            }

            const expense = await expensesTrackerEndpoints.createProjectExpense({
                projectId: projectId.value,
                createExpenseRequest: createExpenseRequestPayload,
            });
            return expense;
        } catch (e) {
            logger.error(e);
            handleUpdateOrCreateError(e);
            return Promise.reject(e);
        }
    }

    async function updateExpense(expenseId: string, form: CreateExpenseRequestFormState): Promise<ExpenseResource> {
        try {
            const {receiptFile, freelancerTaxes, ...restForm} = form;
            const updateExpenseRequestPayload = restForm as CreateExpenseRequest;
            const selectedExpense = expenseList.value.expenses.find((expense) => expense.expenseId === expenseId);

            if (receiptFile.length && receiptFile[0] instanceof File && receiptFile[0].name !== '_____temp_upload_____') {
                const [fileId] = await uploadFiles(receiptFile);
                updateExpenseRequestPayload.receiptId = fileId;
                updateExpenseRequestPayload.receiptName = receiptFile[0].name;
                updateExpenseRequestPayload.receiptUploadDate = new Date();
            } else if (selectedExpense) {
                updateExpenseRequestPayload.receiptId = selectedExpense.receiptId;
                updateExpenseRequestPayload.receiptName = selectedExpense.receiptName;
                updateExpenseRequestPayload.receiptUploadDate = new Date(selectedExpense.receiptUploadDate ?? new Date());
            }

            const expense = await expensesTrackerEndpoints.updateExpense({
                expenseId,
                createExpenseRequest: updateExpenseRequestPayload,
                projectId: projectId.value,
            });
            return expense;
        } catch (e) {
            logger.error(e);
            handleUpdateOrCreateError(e);
            return Promise.reject(e);
        }
    }

    async function deleteExpense(expenseId: string) {
        try {
            await expensesTrackerEndpoints.deleteExpense({projectId: projectId.value, expenseId});
        } catch (e) {
            logger.error(e);
            return Promise.reject(e);
        }
    }

    async function downloadInvoice(expenseId: string) {
        try {
            const result = await expensesTrackerEndpoints.getExpenseInvoice(
                {projectId: projectId.value, expenseId},
                {headers: {accept: 'application/pdf'}},
            );
            const url = window.URL.createObjectURL(result);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'expense-invoice.pdf');
            link.click();
        } catch (e) {
            logger.error(e);
            pushVJoySnackbar({props: {message: t('generic.error.unexpected'), level: 'error'}});
            return Promise.reject(e);
        }
    }

    const displayedCurrency = computed(() => {
        return ProjectCurrencyTypeMapper[projectSettings.value?.currency ?? 'EUR'];
    });

    return {
        expenseList,
        projectSummary,
        loading,
        fetchExpensesForProject,
        formattedExpenses,
        blockedApprovalNotificationCount,
        createExpense,
        updateExpense,
        projectSettings,
        fetchProjectSettings,
        deleteExpense,
        downloadInvoice,
        displayedCurrency,
        vatChoiceInSettings,
        ruleForVatEditing,
        eligibleVatOptions,
    };
});
